'use client'

import { Card, Cell, Checkbox, Container, Spacer, Text } from '@vinted/web-ui'
import { useContext, useEffect } from 'react'
import { useIntl } from 'react-intl'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { itemUploadClickEvent, userInputEvent } from '_libs/common/event-tracker/events'
import { formatCurrencyAmount } from '@marketplace-web/domain/currency-data'
import FaqEntryUrl from 'components/FaqEntryUrl'

import { AccessChannel } from 'constants/access-channel'
import { FaqEntryType } from 'constants/faq-entry'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import {
  getBumpMinimumPrice,
  transformBumpMinimumPriceResponse,
} from '@marketplace-web/vas/vas-data'
import {
  FieldName,
  FieldTrackingName,
  ItemStatus,
  UiStateContext,
  useAttributesContext,
  useFieldVisibility,
} from '@marketplace-web/item-upload/item-upload-data'

type Props = { tempUuid: string }

const BumpItemCheckbox = ({ tempUuid }: Props) => {
  const { track } = useTracking()
  const { locale } = useIntl()
  const {
    screen,
    itemStatus: [itemStatus],
    canBumpItem: [canBumpItem],
  } = useContext(UiStateContext)
  const { attributes, isBumpChecked, setIsBumpChecked } = useAttributesContext()
  const isOnHoliday = useSession().user?.is_on_holiday

  const canShowBumpCheckbox =
    attributes.price !== null && (itemStatus !== ItemStatus.Edit || canBumpItem) && !isOnHoliday

  const {
    fetch: fetchBumpMinimumPrice,
    transformedData: minimumPriceOption,
    isLoading,
  } = useFetch(getBumpMinimumPrice, transformBumpMinimumPriceResponse)

  const translate = useTranslate(
    `item_upload.${minimumPriceOption?.hasFreeBump ? 'free_bump' : 'bump'}`,
  )

  useFieldVisibility({
    fieldName: FieldTrackingName.Bump,
    isVisible: canShowBumpCheckbox,
  })

  useEffect(() => {
    if (!canShowBumpCheckbox) return

    fetchBumpMinimumPrice()
  }, [fetchBumpMinimumPrice, canShowBumpCheckbox])

  function handleCheckboxClick() {
    track(
      userInputEvent({
        screen,
        value: Number(!isBumpChecked).toString(),
        state: 'focus',
        tempUuid,
        target: FieldTrackingName.Bump,
      }),
    )
    setIsBumpChecked(!isBumpChecked)
  }

  function handleLearnMoreClick() {
    track(
      itemUploadClickEvent({
        screen,
        target: ClickableElement.PushUpLearnMode,
        uploadSessionId: tempUuid,
      }),
    )
  }

  const renderCheckbox = () => {
    if (isLoading) {
      return <ContentLoader styling={ContentLoader.Styling.Wide} size={ContentLoader.Size.Large} />
    }

    if (!minimumPriceOption) return null

    const price = formatCurrencyAmount(
      {
        ...minimumPriceOption.payable,
        amount: minimumPriceOption.hasFreeBump ? '0' : minimumPriceOption.payable.amount,
      },
      locale,
    )

    const hasDiscount = parseFloat(minimumPriceOption.discount.amount) > 0

    const priceElement = (
      <Text as="span" theme="amplified">
        {minimumPriceOption.hasFreeBump || hasDiscount ? (
          <>
            {price}
            <Spacer size={Spacer.Size.Small} orientation={Spacer.Orientation.Vertical} />
            <Text
              as="span"
              strikethrough
              theme="muted"
              text={formatCurrencyAmount(minimumPriceOption.total, locale)}
            />
          </>
        ) : (
          translate('price', { price })
        )}
      </Text>
    )

    return (
      <Cell prefix={priceElement}>
        <Checkbox
          name={FieldName.Bump}
          checked={isBumpChecked}
          onClick={handleCheckboxClick}
          testId="bump-checkbox"
          onChange={() => undefined}
        />
      </Cell>
    )
  }

  const renderLearnMoreLink = () => {
    return (
      <FaqEntryUrl
        type={FaqEntryType.Bump}
        accessChannel={AccessChannel.ProductLink}
        render={url => (
          <a href={url} target="_blank" rel="noreferrer" onClick={handleLearnMoreClick}>
            <Text
              as="span"
              text={translate('learn_more')}
              type={Text.Type.Subtitle}
              width={Text.Width.Parent}
              clickable
            />
          </a>
        )}
      />
    )
  }

  if (!canShowBumpCheckbox) return null

  return (
    <>
      <Spacer size={Spacer.Size.XLarge} />
      <Card>
        <Container styling={Container.Styling.Wide}>
          <Cell styling={Cell.Styling.Tight} title={translate('header')} suffix={renderCheckbox()}>
            <Text type={Text.Type.Subtitle} text={translate('body')} as="p" />
            {renderLearnMoreLink()}
          </Cell>
        </Container>
      </Card>
    </>
  )
}

export default BumpItemCheckbox
